/**
 * Writeback-tool authoring HITL — `create_writeback_tool` (from
 * `add_db_write_tool`) and `bind_writeback_tool` (from `use_writeback_tool`).
 * The builder blocks until Apply/Reject; `parseSettledToolComplete` retires
 * the cards when the in-turn wait delivers the outcome (`timed_out` keeps
 * them for the fallback resume rail). Apply/reject reuse the agent-writes
 * endpoints; they key off `tool_use_id` and dispatch on `actionKind`
 * server-side. These are non-graph (`graphAffecting: false`).
 *
 * Importing this module registers both capabilities as a side effect.
 */
import { agentWritesApi } from '../../api/agents/agent-writes'
import { customAgentsApi } from '../../api/agents/custom-agents'
import type {
  InFlightAgentActionProposal,
  InFlightBindWritebackToolProposal,
  InFlightCreateWritebackToolProposal
} from '../../slices/agent-orchestrator-slice'
import type { AppDispatch } from '../../store'
import {
  buildBindWritebackToolEntryFromToolInput,
  buildCreateWritebackToolEntryFromToolInput,
  scheduleHydratePendingAgentWrites
} from '../cypher-write-pending'
import {
  parseSettledProposeOutput,
  readStructuredMutationError,
  registerAgentActionLifecycle,
  type AgentActionApplyOutcome,
  type AgentActionCompleteHydration,
  type AgentActionLifecycleCapability,
  type AgentActionRejectOutcome
} from './lifecycle-registry'

const ADD_DB_WRITE_TOOL_NAME = 'add_db_write_tool'
const USE_WRITEBACK_TOOL_NAME = 'use_writeback_tool'

function readString(record: Record<string, unknown>, key: string): string | null {
  const v = record[key]
  return typeof v === 'string' ? v : null
}

function readNumber(record: Record<string, unknown>, key: string): number | undefined {
  const v = record[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

/** Parse the propose output's async-suspend envelope (timed-out wait / fallback). */
function readProposeOutput(
  parentToolUseId: string,
  output: unknown
): AgentActionCompleteHydration | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) return null
  const obj = output as Record<string, unknown>
  if (obj.pending !== true) return null
  const planId = readString(obj, 'plan_id')
  if (planId === null) return null
  const rawResults = obj.results
  if (!Array.isArray(rawResults)) return null
  const results: Array<{ toolUseId: string; writeId: string; index?: number }> = []
  for (const raw of rawResults) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    const r = raw as Record<string, unknown>
    const toolUseId = readString(r, 'tool_use_id')
    const writeId = readString(r, 'write_id')
    if (toolUseId === null || writeId === null) continue
    results.push({ toolUseId, writeId, index: readNumber(r, 'index') })
  }
  return { parentToolUseId, planId, results }
}

/** Strip double quotes so the summary survives the resume-bubble row regex. */
function sanitizeSummary(text: string): string {
  return text.replace(/"/g, '').trim()
}

function applyWritebackTool(
  toolUseId: string,
  settledMetaFromResult: (applied: Record<string, unknown>) => Record<string, unknown>
): (dispatch: AppDispatch) => Promise<AgentActionApplyOutcome> {
  return async (dispatch): Promise<AgentActionApplyOutcome> => {
    try {
      const result = await dispatch(
        agentWritesApi.endpoints.applyCypherWrite.initiate({
          toolUseId,
          graphAffecting: false
        })
      ).unwrap()
      if (result.ok) {
        const applied = result.result ?? {}
        const configId = typeof applied.config_id === 'string' ? applied.config_id : undefined
        dispatch(
          customAgentsApi.util.invalidateTags([
            { type: 'CustomAgentConfig', id: 'LIST' },
            ...(configId ? [{ type: 'CustomAgentConfig' as const, id: configId }] : [])
          ])
        )
        return { kind: 'success', settledMeta: settledMetaFromResult(applied) }
      }
      return { kind: 'apply_failed', error: result.error ?? 'Writeback tool apply failed.' }
    } catch (err) {
      const refused = readStructuredMutationError(err)
      if (refused !== null) {
        return { kind: 'apply_failed', error: refused.error }
      }
      const message = err instanceof Error ? err.message : 'Writeback tool apply failed.'
      return { kind: 'transport_error', error: message }
    }
  }
}

function rejectWritebackTool(
  toolUseId: string,
  reason: string
): (dispatch: AppDispatch) => Promise<AgentActionRejectOutcome> {
  return async (dispatch): Promise<AgentActionRejectOutcome> => {
    try {
      const result = await dispatch(
        agentWritesApi.endpoints.rejectCypherWrite.initiate({ toolUseId, reason })
      ).unwrap()
      if (result.ok) {
        return { kind: 'success', settledMeta: { rejectionReason: result.reason ?? reason } }
      }
      return { kind: 'reject_failed', error: result.error ?? 'Reject failed.' }
    } catch (err) {
      const refused = readStructuredMutationError(err)
      if (refused !== null) {
        return { kind: 'reject_failed', error: refused.error }
      }
      const message = err instanceof Error ? err.message : 'Reject failed.'
      return { kind: 'transport_error', error: message }
    }
  }
}

export const createWritebackToolLifecycleCapability: AgentActionLifecycleCapability = {
  kind: 'create_writeback_tool',
  toolName: ADD_DB_WRITE_TOOL_NAME,

  parseToolStart(toolUseId, input, startedAtMs) {
    const entry = buildCreateWritebackToolEntryFromToolInput(toolUseId, input, startedAtMs)
    return entry ? [entry] : []
  },

  parseToolComplete(toolUseId, output) {
    return readProposeOutput(toolUseId, output)
  },

  parseSettledToolComplete(output) {
    return parseSettledProposeOutput(output)
  },

  scheduleHydratePending(dispatch, conversationId) {
    scheduleHydratePendingAgentWrites(dispatch, conversationId)
  },

  apply(toolUseId) {
    return applyWritebackTool(toolUseId, (applied) => {
      const id = typeof applied.tool_id === 'string' ? applied.tool_id : undefined
      const name = typeof applied.name === 'string' ? applied.name : undefined
      return {
        createdTool: { id, name },
        bound: applied.bound === true
      }
    })
  },

  reject: rejectWritebackTool,

  buildResumeRowLine(proposal: InFlightAgentActionProposal, displayNumber) {
    if (proposal.kind !== 'create_writeback_tool') {
      return `${displayNumber}. UNDECIDED — "${sanitizeSummary(proposal.summary)}".`
    }
    const row = proposal as InFlightCreateWritebackToolProposal
    const summary = sanitizeSummary(row.summary || `Create DB write tool ${row.name}`)
    const terminal = row.terminalState
    if (terminal?.kind === 'applied') {
      const toolName = row.createdTool?.name ?? row.name
      const bindTail = row.bound || row.configId ? ' and bound to the agent' : ''
      return `${displayNumber}. APPLIED — "${summary}"; tool ${toolName} is in the registry${bindTail}.`
    }
    if (terminal?.kind === 'apply_failed') {
      return `${displayNumber}. APPLY_FAILED — "${summary}"; error: ${terminal.error}.`
    }
    if (terminal?.kind === 'rejected') {
      const reason = row.rejectionReason || 'rejected by user'
      return `${displayNumber}. REJECTED — "${summary}"; reason: "${reason}". The writeback tool was not created.`
    }
    return `${displayNumber}. UNDECIDED — "${summary}".`
  }
}

export const bindWritebackToolLifecycleCapability: AgentActionLifecycleCapability = {
  kind: 'bind_writeback_tool',
  toolName: USE_WRITEBACK_TOOL_NAME,

  parseToolStart(toolUseId, input, startedAtMs) {
    const entry = buildBindWritebackToolEntryFromToolInput(toolUseId, input, startedAtMs)
    return entry ? [entry] : []
  },

  parseToolComplete(toolUseId, output) {
    return readProposeOutput(toolUseId, output)
  },

  parseSettledToolComplete(output) {
    return parseSettledProposeOutput(output)
  },

  scheduleHydratePending(dispatch, conversationId) {
    scheduleHydratePendingAgentWrites(dispatch, conversationId)
  },

  apply(toolUseId) {
    return applyWritebackTool(toolUseId, (applied) => {
      const id = typeof applied.tool_id === 'string' ? applied.tool_id : undefined
      const name = typeof applied.name === 'string' ? applied.name : undefined
      return { boundTool: { id, name } }
    })
  },

  reject: rejectWritebackTool,

  buildResumeRowLine(proposal: InFlightAgentActionProposal, displayNumber) {
    if (proposal.kind !== 'bind_writeback_tool') {
      return `${displayNumber}. UNDECIDED — "${sanitizeSummary(proposal.summary)}".`
    }
    const row = proposal as InFlightBindWritebackToolProposal
    const summary = sanitizeSummary(row.summary || `Bind writeback tool ${row.name ?? row.toolId}`)
    const terminal = row.terminalState
    if (terminal?.kind === 'applied') {
      const toolName = row.boundTool?.name ?? row.name ?? row.toolId
      return `${displayNumber}. APPLIED — "${summary}"; ${toolName} is bound to the agent.`
    }
    if (terminal?.kind === 'apply_failed') {
      return `${displayNumber}. APPLY_FAILED — "${summary}"; error: ${terminal.error}.`
    }
    if (terminal?.kind === 'rejected') {
      const reason = row.rejectionReason || 'rejected by user'
      return `${displayNumber}. REJECTED — "${summary}"; reason: "${reason}". The tool was not bound.`
    }
    return `${displayNumber}. UNDECIDED — "${summary}".`
  }
}

registerAgentActionLifecycle(createWritebackToolLifecycleCapability)
registerAgentActionLifecycle(bindWritebackToolLifecycleCapability)
